/* Flash deal products use the shared BF cart and discount controller. */
(() => {
  if (customElements.get('bf-flash') || !window.BFOfferElement) return;
  class BFFlash extends window.BFOfferElement {
    connectedCallback() {
      super.connectedCallback();
      this.track = this.querySelector('[data-track]');
      this.previous = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      const { signal } = this.lifecycle;
      this.quotaAddPending = false;
      this.previous.addEventListener('click', () => this.move(-1), { signal });
      this.next.addEventListener('click', () => this.move(1), { signal });
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') this.scroll(event.key === 'Home' ? 0 : this.track.scrollWidth);
        else this.move(event.key === 'ArrowLeft' ? -1 : 1);
      }, { signal });
      document.addEventListener('shopify:block:select', (event) => {
        const item = [...this.track.children].find((child) => child.dataset.blockId === event.detail?.blockId);
        if (item) this.scroll(item.offsetLeft - this.track.firstElementChild.offsetLeft, true);
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => this.layout());
      this.resizeObserver.observe(this.track);
      this.layout();
      this.addEventListener('bf:flash:inventory', (event) => {
        if (event.target === this) this.updateInventory(event.detail);
      }, { signal });
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-quota-state="sold-out"] [data-flash-purchase], [data-quota-state="unconfigured"] [data-flash-purchase]')) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { signal, capture: true });
      document.addEventListener('submit', (event) => {
        const modal = event.target.closest('quick-buy-modal[data-bf-flash-owner]');
        if (modal?.dataset.bfFlashOwner !== this.dataset.sectionId || !event.target.matches('form') ||
          !event.target.closest('product-form') || !event.target.elements.id) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.add(event.target, { autoDiscount: false, modal });
      }, { signal, capture: true });
      this.querySelectorAll('[data-product-id]').forEach((card) => this.captureFreshness(card));
      this.clearInventory();
      this.scheduleFeedExpiry();
      this.dispatchInventoryRequest();
      this.inventoryInterval = setInterval(() => { if (!document.hidden) this.requestInventory(); }, 15000);
      document.addEventListener('cart:change', () => this.requestInventory(), { signal });
      document.addEventListener('cart:refresh', () => this.requestInventory(), { signal });
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.requestInventory(); }, { signal });
      window.addEventListener('pageshow', () => this.requestInventory(), { signal });
    }

    layout() {
      const fits = this.track.scrollWidth <= this.track.clientWidth + 2;
      this.previous.hidden = fits;
      this.next.hidden = fits;
    }

    requestInventory() {
      this.refreshShopifyInventory();
      this.dispatchInventoryRequest();
    }

    dispatchInventoryRequest() {
      if (!this.dataset.campaignId) return;
      const productIds = [...new Set([...this.querySelectorAll('[data-product-id]')]
        .filter((card) => !['archived', 'coming-soon'].includes(card.dataset.quotaSource) && card.dataset.quotaLocked !== 'true' && card.dataset.quotaRetired !== 'true')
        .map((card) => card.dataset.productId))];
      if (!productIds.length) return;
      this.dispatchEvent(new CustomEvent('bf:flash:inventory:request', {
        bubbles: true,
        detail: { version: 1, campaignId: this.dataset.campaignId, productIds }
      }));
    }

    async add(form, options = {}) {
      if (this.quotaAddPending) { this.notify(this.message('busyMessage'), 'info'); return; }
      this.quotaAddPending = true;
      const lifecycle = this.lifecycle;
      const button = [...form.elements].find((element) => element.type === 'submit');
      button?.setAttribute('aria-busy', 'true');
      try {
        const confirmed = await this.refreshShopifyInventory();
        if (!this.isConnected || this.lifecycle !== lifecycle || !form.isConnected) return;
        if (!confirmed) { this.notify(this.message('quotaErrorMessage'), 'info'); return; }
        const card = options.modal ? [...this.querySelectorAll('[data-product-id]')].find((item) => item.dataset.productId === options.modal.dataset.bfFlashProduct) : form.closest('[data-product-id]');
        if (!card || ['sold-out', 'unconfigured'].includes(card.dataset.quotaState)) {
          this.notify(this.message(card?.dataset.quotaState === 'unconfigured' ? 'unavailableLabel' : 'soldOutLabel'), 'info');
          return;
        }
        const bar = card.querySelector('[data-quota-bar]');
        const quantity = Number(new FormData(form).get('quantity') || 1);
        const inCart = await this.cartQuantity(card.dataset.productId);
        if (!this.isConnected || this.lifecycle !== lifecycle || !form.isConnected) return;
        this.restoreShopifyInventory(card);
        if (['sold-out', 'unconfigured'].includes(card.dataset.quotaState)) {
          this.notify(this.message(card.dataset.quotaState === 'sold-out' ? 'soldOutLabel' : 'unavailableLabel'), 'info');
          return;
        }
        if (inCart === null || !Number.isSafeInteger(quantity) || quantity <= 0) {
          this.notify(this.message('quotaErrorMessage'), 'info');
          return;
        }
        const remaining = Number(bar.getAttribute('aria-valuenow'));
        const available = Math.max(0, remaining - inCart);
        if (bar.hasAttribute('aria-valuenow') && Number.isSafeInteger(remaining) && quantity > available) {
          this.notify(this.message('quantityLimitMessage').replaceAll('[remaining]', String(available)), 'info');
          return;
        }
        await super.add(form, options);
        if (this.isConnected && this.lifecycle === lifecycle) this.requestInventory();
      } finally {
        if (this.lifecycle === lifecycle) this.quotaAddPending = false;
        button?.removeAttribute('aria-busy');
      }
    }

    async cartQuantity(productId) {
      const request = new AbortController();
      const signal = this.lifecycle.signal;
      const abort = () => request.abort();
      signal.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, 8000);
      try {
        const cart = await this.json(`${this.root()}cart.js`, { signal: request.signal });
        let quantity = 0;
        for (const item of cart.items) {
          if (String(item.product_id) !== productId) continue;
          if (!Number.isSafeInteger(item.quantity) || item.quantity < 0) return null;
          quantity += item.quantity;
        }
        return Number.isSafeInteger(quantity) ? quantity : null;
      } catch { return null; }
      finally { clearTimeout(timeout); signal.removeEventListener('abort', abort); }
    }

    captureFreshness(card) {
      const seconds = Number(card.dataset.quotaValidFor);
      card.bfFeedDeadline = performance.now() + (Number.isFinite(seconds) ? Math.max(0, Math.min(90, seconds)) : 0) * 1000;
    }

    scheduleFeedExpiry() {
      clearTimeout(this.feedExpiryTimer);
      const now = performance.now();
      const deadlines = [...this.querySelectorAll('[data-product-id]')]
        .filter((card) => card.dataset.quotaSource === 'archived' && card.bfFeedDeadline > now)
        .map((card) => card.bfFeedDeadline);
      if (!deadlines.length) return;
      this.feedExpiryTimer = setTimeout(() => {
        this.querySelectorAll('[data-product-id]').forEach((card) => this.restoreShopifyInventory(card));
        this.scheduleFeedExpiry();
      }, Math.max(1, Math.min(...deadlines) - now));
    }

    // Liquid reads the global inventory mirror, never a market quantity as global stock.
    refreshShopifyInventory() {
      if (this.inventoryRefresh) return this.inventoryRefresh;
      const refresh = this.readInventory().finally(() => { if (this.inventoryRefresh === refresh) this.inventoryRefresh = null; });
      this.inventoryRefresh = refresh;
      return this.inventoryRefresh;
    }

    async readInventory() {
      if (!this.dataset.sectionId || !this.isConnected) return false;
      const controller = new AbortController();
      this.inventoryRequest = controller;
      const abort = () => controller.abort();
      const lifecycle = this.lifecycle.signal;
      lifecycle.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, 10000);
      try {
        const url = new URL(window.location.href);
        url.hash = '';
        url.searchParams.delete('sections');
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('_bf_inventory', String(Date.now()));
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) return false;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = [...html.querySelectorAll('bf-flash')].find((element) => element.dataset.sectionId === this.dataset.sectionId);
        if (!this.isConnected || controller.signal.aborted) return false;
        if (!fresh) {
          const emptySection = html.getElementById(`shopify-section-${this.dataset.sectionId}`);
          if (!emptySection || emptySection.textContent.trim()) return false;
          this.querySelectorAll('[data-product-id]').forEach((card) => {
            card.dataset.quotaRetired = 'true';
            this.restoreShopifyInventory(card);
          });
          return true;
        }
        for (const card of this.querySelectorAll('[data-product-id]')) {
          const updated = [...fresh.querySelectorAll('[data-product-id]')].find((item) => item.dataset.quotaKey === card.dataset.quotaKey && item.dataset.productId === card.dataset.productId);
          card.dataset.quotaRetired = String(!updated);
          if (!updated) { this.restoreShopifyInventory(card); continue; }
          card.dataset.quotaTotal = updated.dataset.quotaTotal;
          card.dataset.quotaRemaining = updated.dataset.quotaRemaining;
          card.dataset.quotaSource = updated.dataset.quotaSource || 'shopify';
          card.dataset.quotaLocked = updated.dataset.quotaLocked || 'false';
          card.dataset.quotaBlocked = updated.dataset.quotaBlocked || 'false';
          card.dataset.quotaValidFor = updated.dataset.quotaValidFor || '0';
          this.captureFreshness(card);
          if (card.dataset.quotaSource === 'archived' || card.dataset.quotaLocked === 'true' || !(this.quotaExpires > Date.now())) this.restoreShopifyInventory(card);
        }
        this.scheduleFeedExpiry();
        return true;
      } catch { return false; /* Keep the last displayed state; purchases need a fresh check. */ }
      finally {
        clearTimeout(timeout);
        lifecycle.removeEventListener('abort', abort);
        if (this.inventoryRequest === controller) this.inventoryRequest = null;
      }
    }

    renderInventory(card, total, remaining) {
      const soldOut = remaining === 0;
      card.dataset.quotaState = soldOut ? 'sold-out' : 'active';
      const bar = card.querySelector('[data-quota-bar]');
      bar.setAttribute('aria-valuenow', String(remaining));
      bar.setAttribute('aria-valuemax', String(total));
      bar.style.setProperty('--progress', String(remaining / total));
      card.querySelector('[data-quota-label]').textContent = this.dataset.remainingLabel.replaceAll('[remaining]', String(remaining)).replaceAll('[total]', String(total));
      card.querySelector('[data-quota]').hidden = this.dataset.showProgress !== 'true';
      card.querySelector('[data-quota-actions]').hidden = soldOut;
      card.querySelector('[data-quota-sold-out]').hidden = !soldOut;
      card.querySelector('[data-quota-unavailable]')?.setAttribute('hidden', '');
      if (soldOut) this.closeUnavailableModal(card);
      const purchase = card.querySelector('[data-flash-purchase]');
      if (soldOut) purchase?.setAttribute('aria-disabled', 'true');
      else purchase?.removeAttribute('aria-disabled');
    }

    restoreShopifyInventory(card) {
      const total = Number(card.dataset.quotaTotal);
      const remaining = Number(card.dataset.quotaRemaining);
      if (card.dataset.quotaLocked === 'true' || card.dataset.quotaRetired === 'true') {
        if (Number.isSafeInteger(total) && total > 0) this.renderInventory(card, total, 0);
        else this.renderUnavailable(card, 'sold-out');
        return;
      }
      if (card.dataset.quotaSource === 'archived' && !(card.bfFeedDeadline > performance.now())) {
        this.renderUnavailable(card, 'unconfigured');
        return;
      }
      if (card.dataset.quotaRemaining !== '' && Number.isSafeInteger(total) && total > 0 && Number.isSafeInteger(remaining) && remaining >= 0 && remaining <= total) {
        this.renderInventory(card, total, remaining);
        return;
      }
      this.renderUnavailable(card, card.dataset.quotaBlocked === 'true' ? 'unconfigured' : 'unconnected');
    }

    closeUnavailableModal(card) {
      document.querySelectorAll('quick-buy-modal[data-bf-flash-owner][open]').forEach((modal) => {
        if (modal.dataset.bfFlashOwner === this.dataset.sectionId && modal.dataset.bfFlashProduct === card.dataset.productId) modal.hide?.();
      });
    }

    renderUnavailable(card, state) {
      card.dataset.quotaState = state;
      card.querySelector('[data-quota]').hidden = true;
      card.querySelector('[data-quota-sold-out]').hidden = state !== 'sold-out';
      card.querySelector('[data-quota-actions]').hidden = state !== 'unconnected';
      card.querySelector('[data-quota-unavailable]')?.toggleAttribute('hidden', state !== 'unconfigured');
      const purchase = card.querySelector('[data-flash-purchase]');
      if (state === 'unconnected') purchase?.removeAttribute('aria-disabled');
      else { purchase?.setAttribute('aria-disabled', 'true'); this.closeUnavailableModal(card); }
      const bar = card.querySelector('[data-quota-bar]');
      bar.removeAttribute('aria-valuenow');
      bar.removeAttribute('aria-valuemax');
      bar.style.removeProperty('--progress');
      card.querySelector('[data-quota-label]').textContent = '';
    }

    // A display snapshot only. The future backend must enforce the actual cap.
    updateInventory(snapshot) {
      if (!this.dataset.campaignId || snapshot?.version !== 1 || snapshot.campaignId !== this.dataset.campaignId || !Array.isArray(snapshot.items)) return false;
      const updated = Date.parse(snapshot.updatedAt);
      const expires = Date.parse(snapshot.expiresAt);
      const now = Date.now();
      if (!Number.isFinite(updated) || !Number.isFinite(expires) || updated > now + 5000 || updated < (this.quotaUpdated || 0) || expires <= now || expires <= updated || expires - updated > 300000) return false;
      const entries = new Map();
      for (const item of snapshot.items) {
        if (typeof item?.productId !== 'string' || !/^\d+$/.test(item.productId) || entries.has(item.productId) || !Number.isSafeInteger(item.total) || item.total <= 0 || !Number.isSafeInteger(item.remaining) || item.remaining < 0 || item.remaining > item.total) return false;
        entries.set(item.productId, item);
      }
      this.clearInventory();
      this.quotaUpdated = updated;
      this.quotaExpires = expires;
      for (const card of this.querySelectorAll('[data-product-id]')) {
        if (['archived', 'coming-soon'].includes(card.dataset.quotaSource) || card.dataset.quotaLocked === 'true' || card.dataset.quotaRetired === 'true') continue;
        const item = entries.get(card.dataset.productId);
        if (!item) continue;
        this.renderInventory(card, item.total, item.remaining);
      }
      this.quotaTimer = setTimeout(() => { this.clearInventory(); this.requestInventory(); }, expires - now);
      return true;
    }

    clearInventory() {
      clearTimeout(this.quotaTimer);
      this.quotaExpires = 0;
      for (const card of this.querySelectorAll('[data-product-id]')) {
        this.restoreShopifyInventory(card);
      }
    }

    scroll(left, instant = false) {
      this.track.scrollTo({ left, behavior: instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }

    move(direction) {
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (max <= 2) return;
      const first = this.track.firstElementChild;
      const step = first.getBoundingClientRect().width + (parseFloat(getComputedStyle(this.track).columnGap) || 0);
      const position = this.track.scrollLeft;
      const next = direction > 0 && position >= max - 2 ? 0 : direction < 0 && position <= 2 ? max : Math.max(0, Math.min(max, position + direction * step));
      this.scroll(next);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      clearInterval(this.inventoryInterval);
      clearTimeout(this.feedExpiryTimer);
      this.inventoryRequest?.abort();
      this.inventoryRefresh = null;
      this.clearInventory();
      this.quotaUpdated = 0;
      super.disconnectedCallback();
    }
  }
  customElements.define('bf-flash', BFFlash);
})();
